// =============================================================================
// Brand fonts — shared consumption helpers
// -----------------------------------------------------------------------------
// The Edit Brand wizard's Design System step lets a brand save fonts (curated
// Google fonts + uploaded font files stored in R2 — see `brandFontSchema` in
// shared/schema.ts). This module turns that saved list into the concrete pieces
// downstream renderers (LP Express, Email Express) need:
//   • role selection (which saved font is the heading vs. the body face),
//   • a CSS font-family stack for each role,
//   • the raw font-loading bits (Google stylesheet families + uploaded
//     @font-face rules) so the fonts actually render.
// It is intentionally framework-agnostic (no React / DOM / Node deps) so both
// the client renderers and the server send pipeline can share ONE source of
// truth and stay in lockstep.
// =============================================================================

import type { BrandDesignSystem, BrandFont } from "./schema";
import { GOOGLE_FONT_CATALOG, type GoogleFontCategory } from "./google-fonts";

// Generic fallback tails appended after the brand family so text stays legible
// while the real font loads (and in email clients that can't load web fonts).
export const BRAND_FONT_SANS_FALLBACK =
  'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
export const BRAND_FONT_SERIF_FALLBACK = 'Georgia, "Times New Roman", serif';

// A saved font is usable only if it can actually load: Google fonts always can
// (family name → stylesheet), uploads only when they have a stored R2 URL.
export function isUsableBrandFont(
  font: BrandFont | null | undefined,
): font is BrandFont {
  if (!font || !font.family) return false;
  if (font.source === "google") return true;
  return font.source === "upload" && !!font.url;
}

// Only fonts the image model can plausibly imitate may steer generated ads.
// The model receives just the font's NAME (never the file), so it can
// approximate Google-catalog fonts it has seen in training — but a custom
// uploaded font's name means nothing to it and the render comes back in a
// generic typeface. Uploads are therefore excluded from ad steering
// everywhere: the Ads Express picker, the brand default selector, and the
// server-authoritative resolver below.
export function isAdSteerableBrandFont(
  font: BrandFont | null | undefined,
): font is BrandFont {
  return isUsableBrandFont(font) && font.source === "google";
}

// The stable CSS family name for an uploaded font. Derived from the entry id
// (not the file name) so two uploads sharing a file name never collide — matches
// the design-system preview loader so previews and renders reference the same
// name.
export function uploadedBrandFontFamily(font: Pick<BrandFont, "id">): string {
  return `brand-upload-${font.id}`;
}

// The concrete first family name a saved font contributes to a CSS stack:
// the Google family for "google", the derived upload family for "upload".
export function brandFontFamilyName(font: BrandFont): string {
  return font.source === "upload" ? uploadedBrandFontFamily(font) : font.family;
}

// Build a CSS font-family stack ("<family>", <fallback>) for a saved font.
export function brandFontCssStack(
  font: BrandFont,
  fallback: string = BRAND_FONT_SANS_FALLBACK,
): string {
  return `"${brandFontFamilyName(font)}", ${fallback}`;
}

// ── Role selection ───────────────────────────────────────────────────────────
// A saved font's `label` is a role hint chosen from a small fixed dropdown
// (Default / Headline / Body / None). Older brands may still carry free-text
// labels ("Header", "title", …), so we match on hint keywords for backward
// compatibility. A "Default" font fills BOTH the heading and body slot, while an
// explicit Headline/Body on another font wins for that specific slot. Anything
// unmatched falls back to list order so a brand with a single (or unlabeled)
// font still themes sensibly.
const DEFAULT_LABEL_HINTS = ["default"];
const HEADING_LABEL_HINTS = ["head", "title", "display", "hero", "h1"];
const BODY_LABEL_HINTS = ["body", "text", "paragraph", "copy", "content"];
// "Buttons" role: this font is the brand's CTA/button-label face. It is applied
// through the brand CTA style pipeline (shared/brand-cta-style.ts) — an explicit
// ctaStyle.fontId always wins; this role is the fallback when none is picked.
// Deliberately NOT consulted by selectBrandFontRoles below: a Buttons-labeled
// font stays exactly as fallback-eligible for heading/body as an unlabeled one,
// so existing heading/body resolution is untouched by this role existing.
const BUTTON_LABEL_HINTS = ["button", "cta"];

function labelMatches(label: string, hints: string[]): boolean {
  const l = (label || "").toLowerCase();
  return hints.some((h) => l.includes(h));
}

// ── Role dropdown model (shared by the Brand Design System editor) ────────────
// `none` = unassigned (stored as an empty label). It is intentionally NOT a
// selectable option — the dropdown only offers real roles, and fonts are
// removed via the trash button rather than being parked as None. `none` still
// exists so legacy/unrecognized labels resolve to a placeholder (no overwrite)
// until the user explicitly picks a role.
export type BrandFontRole = "none" | "default" | "headline" | "body" | "button";

export const BRAND_FONT_ROLE_OPTIONS: { value: BrandFontRole; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "headline", label: "Headline" },
  { value: "body", label: "Body" },
  { value: "button", label: "Buttons" },
];

// Canonical stored `label` for a chosen role. `none` clears the label.
export function brandFontLabelForRole(role: BrandFontRole): string {
  switch (role) {
    case "default":
      return "Default";
    case "headline":
      return "Headline";
    case "body":
      return "Body";
    case "button":
      return "Buttons";
    case "none":
    default:
      return "";
  }
}

// Resolve a stored (possibly free-text/legacy) label to the closest dropdown
// role for display. Check Default first so "default" never gets mistaken for a
// heading/body hint. Unrecognized labels map to `none` — the stored value is
// left untouched until the user explicitly picks a new role.
export function brandFontRoleFromLabel(
  label: string | null | undefined,
): BrandFontRole {
  const l = (label || "").trim();
  if (!l) return "none";
  if (labelMatches(l, DEFAULT_LABEL_HINTS)) return "default";
  if (labelMatches(l, BUTTON_LABEL_HINTS)) return "button";
  if (labelMatches(l, HEADING_LABEL_HINTS)) return "headline";
  if (labelMatches(l, BODY_LABEL_HINTS)) return "body";
  return "none";
}

// The brand's Buttons-role font (CTA/button labels), or null when no usable
// saved font carries the role. First match wins, mirroring heading/body.
export function selectBrandButtonFont(
  fonts: BrandFont[] | null | undefined,
): BrandFont | null {
  const list = (fonts ?? []).filter(isUsableBrandFont);
  return list.find((f) => labelMatches(f.label, BUTTON_LABEL_HINTS)) ?? null;
}

export interface BrandFontRoles {
  heading: BrandFont | null;
  body: BrandFont | null;
}

// Pick the heading + body face from a brand's saved fonts. Explicit Headline /
// Body entries win; a "Default" font fills any slot left unassigned; remaining
// roles fall back to list order, preferring a distinct entry for body when more
// than one usable font exists, else reusing the heading font for both.
export function selectBrandFontRoles(
  fonts: BrandFont[] | null | undefined,
): BrandFontRoles {
  const list = (fonts ?? []).filter(isUsableBrandFont);
  if (list.length === 0) return { heading: null, body: null };

  const heading =
    list.find((f) => labelMatches(f.label, HEADING_LABEL_HINTS)) ?? null;
  const body =
    list.find((f) => labelMatches(f.label, BODY_LABEL_HINTS)) ?? null;
  const defaultFont =
    list.find((f) => labelMatches(f.label, DEFAULT_LABEL_HINTS)) ?? null;

  const resolvedHeading =
    heading ?? defaultFont ?? list.find((f) => f !== body) ?? list[0];
  const resolvedBody =
    body ?? defaultFont ?? list.find((f) => f !== resolvedHeading) ?? resolvedHeading;

  return { heading: resolvedHeading, body: resolvedBody };
}

// ── Font loading bits ────────────────────────────────────────────────────────
// The distinct Google families + uploaded fonts a set of roles needs loaded.
export function collectBrandFontLoads(roles: BrandFontRoles): {
  googleFamilies: string[];
  uploadedFonts: BrandFont[];
} {
  const picked = [roles.heading, roles.body].filter(isUsableBrandFont);
  const googleFamilies: string[] = [];
  const uploadedFonts: BrandFont[] = [];
  const seenGoogle = new Set<string>();
  const seenUpload = new Set<string>();
  for (const f of picked) {
    if (f.source === "google") {
      if (!seenGoogle.has(f.family)) {
        seenGoogle.add(f.family);
        googleFamilies.push(f.family);
      }
    } else if (f.url && !seenUpload.has(f.id)) {
      seenUpload.add(f.id);
      uploadedFonts.push(f);
    }
  }
  return { googleFamilies, uploadedFonts };
}

// Map a saved upload's format/fileName to a CSS @font-face `format(...)` hint.
export function brandFontFormat(font: BrandFont): string | null {
  const raw = `${font.format || ""} ${font.fileName || ""}`.toLowerCase();
  if (raw.includes("woff2")) return "woff2";
  if (raw.includes("woff")) return "woff";
  if (raw.includes("otf") || raw.includes("opentype")) return "opentype";
  if (raw.includes("ttf") || raw.includes("truetype")) return "truetype";
  return null;
}

// Uploaded fonts live in R2, which sends no CORS headers; browsers always fetch
// fonts in CORS mode, so absolute URLs must load through our same-origin proxy.
// (No-op for already-relative URLs.)
export function brandFontProxyUrl(url: string): string {
  return /^https?:\/\//i.test(url)
    ? `/api/brand-font?url=${encodeURIComponent(url)}`
    : url;
}

// A single @font-face rule for an uploaded font. `srcUrl` is passed in so the
// caller decides whether to proxy (browser) or use the raw R2 URL (server).
export function brandFontFaceRule(font: BrandFont, srcUrl: string): string {
  const fmt = brandFontFormat(font);
  return `@font-face { font-family: "${uploadedBrandFontFamily(font)}"; src: url("${srcUrl}")${
    fmt ? ` format("${fmt}")` : ""
  }; font-display: swap; }`;
}

// ── Licensing / copyright (uploaded fonts) ───────────────────────────────────
// Uploaded (custom / proprietary) fonts carry a licensing status + an explicit
// "I have the rights" confirmation. Google fonts are inherently open-licensed
// and are never flagged. This is a SOFT compliance signal — it warns, it never
// blocks a render.
export type BrandFontLicenseStatus = "licensed" | "open_source" | "unknown";

export const BRAND_FONT_LICENSE_OPTIONS: {
  value: BrandFontLicenseStatus;
  label: string;
}[] = [
  { value: "licensed", label: "Licensed for use" },
  { value: "open_source", label: "Open-source / free license" },
  { value: "unknown", label: "Unknown" },
];

type BrandFontLicenseFields = Pick<
  BrandFont,
  "source" | "licenseStatus" | "rightsConfirmed"
>;

// A saved font is "flagged" (licensing not cleared) when it's an UPLOAD whose
// status is unknown OR whose usage rights haven't been confirmed. Google fonts
// are never flagged, so existing brands (which only ever had google + uploads
// pre-dating this field) surface warnings only on genuinely-uncleared uploads.
export function isBrandFontLicenseFlagged(
  font: BrandFontLicenseFields | null | undefined,
): boolean {
  if (!font) return false;
  if (font.source === "google") return false;
  const status = font.licenseStatus ?? "unknown";
  return status === "unknown" || !font.rightsConfirmed;
}

// Human-readable warning for a flagged font (null when not flagged).
export function brandFontLicenseWarning(
  font: BrandFontLicenseFields | null | undefined,
): string | null {
  if (!isBrandFontLicenseFlagged(font)) return null;
  const status = font?.licenseStatus ?? "unknown";
  if (status === "unknown") {
    return "License status unknown — confirm you have the rights to use this font in generated ads.";
  }
  return "Usage rights not confirmed — confirm you have the rights to use this font in generated ads.";
}

// A short natural-language style descriptor for a Google font category, used to
// help the image model approximate an unfamiliar family by its overall look
// (consumed by the brand CTA style resolver in shared/brand-cta-style.ts).
export function googleCategoryStyleDescription(
  category: GoogleFontCategory | string | null | undefined,
): string | null {
  switch (category) {
    case "serif":
      return "an elegant serif typeface";
    case "sans-serif":
      return "a clean sans-serif typeface";
    case "display":
      return "a bold display typeface";
    case "handwriting":
      return "a handwritten script typeface";
    case "monospace":
      return "a monospaced typeface";
    default:
      return null;
  }
}


